/**
 * This module provides a global caching service for API calls to prevent duplicate requests
 */

// Create a namespace on the window object for caching API responses
window.MYP_API_CACHE = window.MYP_API_CACHE || {};

// Helper to create a cache entry if it doesn't exist
const ensureCacheEntry = (cacheKey) => {
  if (!window.MYP_API_CACHE[cacheKey]) {
    window.MYP_API_CACHE[cacheKey] = {
      data: null,
      timestamp: null,
      isLoading: false,
      callbacks: [],
      promise: null
    };
  }
  return window.MYP_API_CACHE[cacheKey];
};

class GlobalCacheService {
  constructor() {
    // Default cache expiration time is 5 minutes
    this.defaultExpirationMs = 5 * 60 * 1000;
  }

  /**
   * Cache API calls to prevent duplicates
   * @param {string} cacheKey - Unique identifier for this API call
   * @param {Function} apiCall - Function that makes the actual API call and returns a Promise
   * @param {number} expirationMs - Optional cache expiration time in milliseconds
   * @returns {Promise} Promise that resolves with API response
   */
  cacheApiCall(cacheKey, apiCall, expirationMs = this.defaultExpirationMs) {
    const cacheEntry = ensureCacheEntry(cacheKey);
    
    // If we have cached data that hasn't expired, return it
    if (
      cacheEntry.data && 
      cacheEntry.timestamp && 
      (Date.now() - cacheEntry.timestamp) < expirationMs
    ) {
      return Promise.resolve(cacheEntry.data);
    }
    
    // If we're already loading this data, return the existing promise
    if (cacheEntry.isLoading && cacheEntry.promise) {
      return cacheEntry.promise;
    }
    
    // Start a new request
    cacheEntry.isLoading = true;
    cacheEntry.promise = apiCall()
      .then(response => {
        cacheEntry.data = response;
        cacheEntry.timestamp = Date.now();
        cacheEntry.isLoading = false;
        
        // Notify all callbacks
        cacheEntry.callbacks.forEach(callback => callback(response));
        cacheEntry.callbacks = [];
        
        return response;
      })
      .catch(error => {
        // On error, clear cache entry
        cacheEntry.isLoading = false;
        cacheEntry.promise = null;
        throw error;
      });
    
    return cacheEntry.promise;
  }
  
  /**
   * Register a callback to be notified when data is available for a specific cache key
   * @param {string} cacheKey - Unique identifier for the API call
   * @param {Function} callback - Function to call when data is available
   */
  onDataAvailable(cacheKey, callback) {
    const cacheEntry = ensureCacheEntry(cacheKey);
    
    if (cacheEntry.data) {
      // If data is already available, call the callback immediately
      callback(cacheEntry.data);
    } else {
      // Otherwise add to the callback queue
      cacheEntry.callbacks.push(callback);
    }
  }
  
  /**
   * Clear the cache for a specific cache key
   * @param {string} cacheKey - Unique identifier for the API call to clear
   */
  clearCache(cacheKey) {
    if (window.MYP_API_CACHE[cacheKey]) {
      window.MYP_API_CACHE[cacheKey].data = null;
      window.MYP_API_CACHE[cacheKey].timestamp = null;
      window.MYP_API_CACHE[cacheKey].promise = null;
    }
  }
  
  /**
   * Clear all cached data
   */
  clearAllCache() {
    window.MYP_API_CACHE = {};
  }
  
  /**
   * Clear all caches with keys that start with a specific prefix
   * @param {string} prefix - The prefix to match against cache keys
   */
  clearCachesByPrefix(prefix) {
    if (!prefix) return;
    
    Object.keys(window.MYP_API_CACHE || {}).forEach(key => {
      if (key.startsWith(prefix)) {
        this.clearCache(key);
      }
    });
  }
  
  /**
   * Check if data is available in the cache
   * @param {string} cacheKey - Unique identifier for the API call
   * @returns {boolean} True if data is available in the cache
   */
  hasData(cacheKey) {
    return !!(
      window.MYP_API_CACHE[cacheKey] && 
      window.MYP_API_CACHE[cacheKey].data
    );
  }
  
  /**
   * Get data from the cache if available
   * @param {string} cacheKey - Unique identifier for the API call
   * @returns {any} Cached data or null if not available
   */
  getData(cacheKey) {
    if (this.hasData(cacheKey)) {
      return window.MYP_API_CACHE[cacheKey].data;
    }
    return null;
  }
}

// Create a singleton instance
const globalCacheService = new GlobalCacheService();

export default globalCacheService;